import router from '@/router';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { useLanguageStore } from '@/stores/language';
import { initializeAuthGuards } from '@/middleware/auth.js';
import { permissionDirective, roleDirective } from '@/directives/permission.js';
import CanDo from '@/components/ui/CanDo.vue';
import { installGlobalErrorLogging } from '@/utils/logger.js';
import { installAccessibilityGuard, primeVueButtonPassThrough } from '@/utils/accessibility.js';

import '@/assets/styles.scss';
import '@/assets/project-tabs-dark.css';
import '@/assets/strategy-forms-dark.css';

// Apply the persisted color scheme before Vue renders to prevent a light-theme
// flash and keep PrimeVue's dark selector in sync from the first frame.
try {
    const savedLayout = JSON.parse(localStorage.getItem('nawa-layout-config') || '{}');
    document.documentElement.classList.toggle('app-dark', savedLayout.darkTheme === true);
} catch {
    document.documentElement.classList.remove('app-dark');
}

const app = createApp(App);
const pinia = createPinia();

installGlobalErrorLogging(app);

app.use(pinia);
app.use(router);

// Initialize authentication guards after Pinia is set up
initializeAuthGuards(router);

// Register global permission directives
app.directive('permission', permissionDirective);
app.directive('role', roleDirective);

// Register CanDo as a global component
app.component('CanDo', CanDo);

const MyPreset = definePreset(Aura, {
    semantic: {
        select: {
            borderRadius: '{border.radius.lg}',
            height: '2rem'
        },
        formField: {
            borderRadius: '{border.radius.md}'
        },
        primary: {
            50: '#effafa',
            100: '#d7f3f3',
            200: '#b4e7e8',
            300: '#80d4d7',
            400: '#46bbc0',
            500: '#22a9ae',
            600: '#168c91',
            700: '#177176',
            800: '#195b5f',
            900: '#194c50',
            950: '#092d31'
        }
    }
});
app.use(PrimeVue, {
    pt: {
        button: primeVueButtonPassThrough
    },
    theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: '.app-dark'
        }
    }
});
app.use(ToastService);
app.use(ConfirmationService);

// Initialize language store
const languageStore = useLanguageStore();
languageStore.initializeLanguage();

// Mount the app
app.mount('#app');
installAccessibilityGuard();

// Remove preloader with smooth fade-out animation
setTimeout(() => {
    const preloader = document.querySelector('.loader-container');
    if (preloader) {
        // Fade out animation
        preloader.style.opacity = '0';
        
        // Remove from DOM after fade completes
        setTimeout(() => {
            if (preloader.parentNode) {
                preloader.parentNode.removeChild(preloader);
            }
        }, 500); // Match the CSS transition duration
    }
}, 300); // Small delay to ensure app is fully rendered
